import {
  AspectRatio,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { styled } from "@mui/system";
import { useState } from "react";
import { Character } from "../data/characterData";

interface FlipCardProps {
  character: Character;
}

const FlipCard = ({ character }: FlipCardProps) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const { mode } = useColorScheme();
  const isDark = mode === "dark";

  // Get color based on affiliation with more subtle colors
  const getAffiliationColor = (affiliation: string) => {
    const affiliationLower = affiliation.toLowerCase();

    if (affiliationLower.includes("migora")) return "rgba(255, 215, 0, 0.6)"; // Golden
    if (affiliationLower.includes("hydaria")) return "rgba(239, 68, 68, 0.6)"; // Red
    if (affiliationLower.includes("rigata")) return "rgba(34, 197, 94, 0.6)"; // Green
    if (affiliationLower.includes("vara")) return "rgba(168, 85, 247, 0.6)"; // Purple
    if (affiliationLower.includes("riverlands"))
      return "rgba(59, 130, 246, 0.6)"; // Blue
    if (affiliationLower.includes("gaudy")) return "rgba(236, 72, 153, 0.6)"; // Pink
    if (affiliationLower.includes("osgara")) return "rgba(156, 163, 175, 0.6)"; // Gray

    return "rgba(120, 119, 198, 0.6)"; // Default magical purple
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const CardInner = styled("div")({
    position: "relative",
    width: "100%",
    height: "100%",
    textAlign: "center",
    transformStyle: "preserve-3d",
    WebkitTransformStyle: "preserve-3d",
  });

  // Front face - visible when not flipped
  const CardFront = styled("div")({
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "12px",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease",
    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
    opacity: isFlipped ? 0 : 1,
    zIndex: isFlipped ? 1 : 2,
  });

  // Back face - visible when flipped
  const CardBack = styled("div")({
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "12px",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease",
    transform: isFlipped ? "rotateY(0deg)" : "rotateY(-180deg)",
    opacity: isFlipped ? 1 : 0,
    zIndex: isFlipped ? 2 : 1,
  });

  const affiliationColor = getAffiliationColor(character.affiliation);

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: "300px",
        maxWidth: "340px",
        height: 480,
        cursor: "pointer",
        background: isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.6)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(10px)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        perspective: "1000px",
        WebkitPerspective: "1000px",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isDark
            ? "0 12px 24px rgba(0, 0, 0, 0.4)"
            : "0 12px 24px rgba(0, 0, 0, 0.15)",
          border: `1px solid ${affiliationColor}`,
        },
      }}
      onClick={handleFlip}
    >
      {/* Subtle background gradient */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${affiliationColor}05 0%, transparent 50%)`,
          zIndex: -1,
        }}
      />

      <CardInner>
        <CardFront>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              height: "100%",
              p: 3,
              gap: 2,
            }}
          >
            {/* Character Name */}
            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                letterSpacing: "0.02em",
                textAlign: "center",
              }}
            >
              {character.name}
            </Typography>

            {/* Character Portrait */}
            <Box
              sx={{
                position: "relative",
                flex: "0 0 auto",
              }}
            >
              <AspectRatio
                ratio="1"
                sx={{
                  width: "160px",
                  height: "160px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `2px solid ${affiliationColor}`,
                  boxShadow: `0 4px 16px ${affiliationColor}30`,
                }}
              >
                <Avatar
                  alt={character.name}
                  src={character.imageSrc}
                  sx={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </AspectRatio>
            </Box>

            {/* Character Info */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                flex: "1 1 auto",
                justifyContent: "center",
              }}
            >
              <Typography
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(30, 41, 59, 0.8)",
                  fontStyle: "italic",
                  textAlign: "center",
                  letterSpacing: "0.01em",
                }}
              >
                {character.rank}
              </Typography>

              <Chip
                variant="outlined"
                sx={{
                  background: `${affiliationColor}20`,
                  border: `1px solid ${affiliationColor}`,
                  color: isDark ? "white" : "#1e293b",
                  fontWeight: 400,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.75rem",
                  fontFamily: "'Cinzel', serif",
                }}
                size="sm"
              >
                {character.affiliation}
              </Chip>

            </Box>
          </CardContent>
        </CardFront>

        <CardBack>
          <CardContent
            sx={{
              height: "100%",
              p: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Character Name */}
            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                letterSpacing: "0.02em",
                textAlign: "center",
                flex: "0 0 auto",
              }}
            >
              {character.name}
            </Typography>

            {/* Biography */}
            <Box
              sx={{
                flex: "1 1 auto",
                width: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                overflowY: "auto",
                px: 1,
                py: 2,
              }}
            >
              <Typography
                level="body-md"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.5,
                  textAlign: "justify",
                  fontSize: "0.85rem",
                }}
              >
                {character.biography}
              </Typography>
            </Box>
          </CardContent>
        </CardBack>
      </CardInner>
    </Card>
  );
};

export default FlipCard;
